if (!customElements.get('sa-video-swiper')) {
  class SaVideoSwiper extends HTMLElement {
    connectedCallback() {
      this.counterEl = this.querySelector('[data-vs-counter]');
      this.progressEl = this.querySelector('[data-vs-progress]');
      this.total = parseInt(this.getAttribute('data-total'), 10) || 0;

      this.initSwiper();
      this.initScrollAnimation();
      this.setupVideoObserver();
    }

    setupVideoObserver() {
      if (this.videoObserver) this.videoObserver.disconnect();

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { root: null, rootMargin: '0px', threshold: 0.1 }
      );

      this.querySelectorAll('video').forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.loop = true;
        video.removeAttribute('autoplay');
        this.videoObserver.observe(video);
      });
    }

    async initSwiper() {
      if (!window.Swiper) {
        let attempts = 0;
        const check = setInterval(() => {
          attempts++;
          if (window.Swiper) {
            clearInterval(check);
            this.initSwiperInstance();
          } else if (attempts > 50) {
            clearInterval(check);
            console.error('[sa-video-swiper] Swiper not found');
          }
        }, 100);
        return;
      }
      this.initSwiperInstance();
    }

    initSwiperInstance() {
      const swiperContainer = this.querySelector('.swiper');
      if (!swiperContainer) return;

      const self = this;

      this.swiper = new window.Swiper(swiperContainer, {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
        loop: true,
        navigation: {
          nextEl: this.querySelector('.sa-video-swiper__next'),
          prevEl: this.querySelector('.sa-video-swiper__prev'),
        },
        pagination: {
          el: this.querySelector('.sa-video-swiper__pagination'),
          clickable: true,
        },
        breakpoints: {
          1024: { slidesPerView: 2, slidesPerGroup: 1 },
        },
        on: {
          init() {
            self.updateNav(this);
          },
          slideChange() {
            self.updateNav(this);
          },
          resize() {
            self.updateNav(this);
          },
        },
      });

      // Fallback in case `init` fired before the handler was attached.
      this.updateNav(this.swiper);
    }

    // Sync the "n/total" counter and the progress-bar width with the active slide.
    updateNav(swiper) {
      if (!swiper) return;
      const total = this.total || swiper.slides.length;
      const slidesPerView = swiper.params.slidesPerView;
      const numericSlidesPerView = typeof slidesPerView === 'number' ? slidesPerView : 1;
      const visible = Math.max(1, Math.ceil(numericSlidesPerView));
      const active = Math.min(swiper.activeIndex || 0, Math.max(total - visible, 0));
      const isDesktopTwoUp = visible > 1 && window.matchMedia('(min-width: 1024px)').matches;
      const displayIndex = Math.min(active + (isDesktopTwoUp ? visible : 1), total);

      if (this.counterEl) {
        this.counterEl.textContent = `${displayIndex}/${total}`;
      }

      if (this.progressEl) {
        // Progress tracks snap positions (e.g. 4 slides @ 2-per-view → 3 stops),
        // so the bar reaches 100% at the final reachable slide.
        const snaps = (swiper.snapGrid && swiper.snapGrid.length) || total;
        const snapIndex = Math.min(swiper.snapIndex || 0, snaps - 1);
        const progress = (snapIndex + 1) / snaps;
        this.progressEl.style.transform = `scaleX(${progress})`;
      }

    }

    initScrollAnimation() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.add('is-visible');
              observer.disconnect();
            }
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(this);
    }

    disconnectedCallback() {
      if (this.swiper) this.swiper.destroy();
      if (this.videoObserver) this.videoObserver.disconnect();
    }
  }

  customElements.define('sa-video-swiper', SaVideoSwiper);
}
